<template>
  <div>
    <div v-if="!isSnapshot">
      <store-provider
        v-bind="$options.storeInfo"
        :params="params"
        is-paged
      >
        <template v-slot="{ state: links }">
          <mds-row>
            <mds-col :push="10">
              <h4 class="visited_link_tab__totalelements">
                Total Elements: {{ links('totalElements') }}
              </h4>
            </mds-col>
          </mds-row>
          <mds-table row-hover show-sortable>
            <mds-thead>
              <mds-th
                v-for="header in $options.headers"
                :key="header.fieldName"
                :style="header.style"
                :sortable="header.sortable"
                :sorted="isSorted(header.fieldName)"
                @mds-th-sort-changed="handleSortEvent(header.fieldName, $event)"
              >
                {{ header.text }}
              </mds-th>
            </mds-thead>
            <mds-tbody>
              <mds-tr v-for="link in links('job')" :key="link.id">
                <mds-td v-for="header in $options.headers" :key="header.fieldName" :style="header.style">
                  <template v-if="header.fieldName === 'link'">
                    <a :href="link.link" target="_blank">{{ link.link | decodeLink }}</a>
                  </template>
                  <template v-else-if="header.fieldName === 'active'">
                    <mds-switch label="Active" hidden-label :checked="link.enabledNextExecution" @change="setNextExecution(link)" />
                  </template>
                  <template v-else-if="header.fieldName === 'lastIndexedDate'">
                    {{ link[header.fieldName] | formatDate }}
                  </template>
                  <template v-else>
                    {{ link[header.fieldName] || '—' }}
                  </template>
                </mds-td>
              </mds-tr>
            </mds-tbody>
          </mds-table>
        </template>
      </store-provider>
    </div>
    <div v-else>
      <mds-row>
        <mds-col :push="10">
          <h4 class="visited_link_tab__totalelements">Total Elements: {{ totalElementsSnapshot }}</h4>
        </mds-col>
      </mds-row>
      <mds-table row-hover>
        <mds-thead>
          <mds-th
            v-for="header in $options.headers"
            :key="header.fieldName"
            :style="header.style"
            :sortable="header.sortable"
          >
            {{ header.text }}
          </mds-th>
        </mds-thead>
        <mds-tbody>
          <mds-tr v-for="link in snapshotLinks" :key="link.id || link.uuid || link.link">
            <mds-td v-for="header in $options.headers" :key="header.fieldName" :style="header.style">
              <template v-if="header.fieldName === 'link'">
                <a :href="link.link" target="_blank">{{ link.link | decodeLink }}</a>
              </template>
              <template v-else-if="header.fieldName === 'active'">
                <mds-switch label="Active" hidden-label :checked="link.enabledNextExecution" @change="setNextExecution(link)" />
              </template>
              <template v-else-if="header.fieldName === 'lastIndexedDate'">
                <template v-if="link.s3FilePath">
                  <button
                    type="button"
                    class="visited_link_tab__snapshot-report"
                    aria-label="Open snapshot document report"
                    @click="handleSnapshotLastIndexedDateClick(link)"
                  >
                    {{ link[header.fieldName] | formatDate }}
                  </button>
                </template>
                <template v-else>
                  {{ link[header.fieldName] | formatDate }}
                </template>
              </template>
              <template v-else>
                {{ link[header.fieldName] || '—' }}
              </template>
            </mds-td>
          </mds-tr>
        </mds-tbody>
      </mds-table>
    </div>
  </div>
</template>

<script>
import {
  MdsTable,
  MdsThead,
  MdsTh,
  MdsTbody,
  MdsTr,
  MdsTd
} from '@mds/data-table';
import MdsSwitch from '@mds/switch';
import StoreProvider from '@/components/common/StoreProvider';
import { formatDate } from '@/utils/global.util';
import { parseSorted, parseSortParams } from '@/utils/sort.util';
import { patchVisitedLink } from '@/services/api/job.service';
import { VISITED_LINKS_HEADERS } from '@/constants/jobs.constant';
import { MODULE, COMMON } from '@/constants/store.constant';
import ROUTE from '@/constants/routes.constant';
import http from '@/plugins/http';
import { getPrEcDocumentReport } from '@/services/api/pr-ec.service';

export default {
  name: 'VisitedLinksTab',
  components: {
    MdsTable,
    MdsSwitch,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    StoreProvider,
  },
  filters: {
    formatDate,
    decodeLink (url) {
      return decodeURI(url);
    },
  },
  props: {
    linkType: {
      type: String,
      required: true,
    },
  },
  computed: {
    isSnapshot () {
      return this.$route && this.$route.name === ROUTE.SNAPSHOT_JOB;
    },
    params () {
      return { ...this.$route.params, type: this.linkType };
    },
  },
  methods: {
    async setNextExecution (link) {
      await patchVisitedLink(this.$route.params, {
        link: link.link,
        enabledNextExecution: !link.enabledNextExecution,
      });
    },
    async handleSortEvent (fieldName) {
      const value = parseSortParams(this.$store.state[MODULE.JOB], fieldName);
      this.$store.commit(`${MODULE.JOB}/${COMMON.UPDATE_PARAM}`, {
        name: 'sort',
        value,
      });
      await this.$store.dispatch(`${MODULE.JOB}/${COMMON.FETCH_DATA}`, {
        params: this.params,
        shouldReset: true,
      });
    },
    isSorted (fieldName) {
      return parseSorted(this.$store.state[MODULE.JOB], fieldName);
    },
    async fetchSnapshotVisitedLinks () {
      try {
        const { prEcJobId } = this.$route.params || {};
        const indexType = (this.$route.query && (this.$route.query['index-type'] || this.$route.query.indexType)) || null;
        const { data } = await http.get(`/pr-ec/jobs/${prEcJobId}`, indexType ? { params: { 'index-type': indexType } } : undefined);
        const rows = data && data.content ? data.content : Array.isArray(data) ? data : [];
        const normalized = rows.map(r => ({
          ...r,
          // Map snapshot-specific fields to table headers
          downloadedFilesCount: typeof r.downloadedFilesCount !== 'undefined' ? r.downloadedFilesCount : r.newFileCount,
          fileErrors: typeof r.fileErrors !== 'undefined' ? r.fileErrors : r.errorMessage,
          lastIndexedDate: typeof r.lastIndexedDate !== 'undefined' ? r.lastIndexedDate : r.processEndTime,
          statusCode: typeof r.statusCode !== 'undefined' ? r.statusCode : r.status,
          enabledNextExecution: typeof r.enabledNextExecution !== 'undefined' ? r.enabledNextExecution : !!r.enabled,
        }));
        this.snapshotLinks = normalized;
        this.totalElementsSnapshot = normalized.length;
      } catch (e) {
        this.snapshotLinks = [];
        this.totalElementsSnapshot = 0;
      }
    },
    async handleSnapshotLastIndexedDateClick (link) {
      if (!link || !link.s3FilePath) {
        return;
      }

      try {
        const { data } = await getPrEcDocumentReport(link.s3FilePath);
        const blob = new Blob([data], { type: 'application/pdf' });
        const fileUrl = window.URL.createObjectURL(blob);
        window.open(fileUrl, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(fileUrl), 10000);
      } catch (error) {
        console.error('Failed to open snapshot document report', error);
      }
    },
  },
  headers: VISITED_LINKS_HEADERS,
  storeInfo: {
    module: MODULE.JOB,
    action: COMMON.FETCH_DATA,
    getter: COMMON.GET_STATE,
  },
  data () {
    return {
      snapshotLinks: [],
      totalElementsSnapshot: 0,
    };
  },
  mounted () {
    if (this.isSnapshot) {
      this.fetchSnapshotVisitedLinks();
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@mds/typography';

.visited_link_tab {
  &__totalelements {
    @include mds-eyebrow-heading();
  }
}

.visited_link_tab__snapshot-report {
  background: none;
  border: none;
  padding: 0;
  color: #005ea5;
  cursor: pointer;
  text-decoration: underline;
  font: inherit;
}

.visited_link_tab__snapshot-report:focus {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}
</style>
